/**
 * Inject AI-generated images into the product media carousel (Swiper or legacy slideshow).
 * Only one AI slide is kept at a time; selecting history replaces the previous AI preview.
 */
(function () {
  "use strict";

  let aiSlideCounter = 0;

  const DESIGN_BADGE_ICON = "✨";
  const DESIGN_BADGE_LABEL = "Your Design";
  const DESIGN_BADGE_MAIN_HTML = `<span class="product-media-design-badge" aria-hidden="true"><span class="product-media-design-badge__icon">${DESIGN_BADGE_ICON}</span><span class="product-media-design-badge__label">${DESIGN_BADGE_LABEL}</span></span>`;
  const DESIGN_BADGE_THUMBNAIL_HTML = `<span class="slideshow-controls__thumbnail-badge product-media-design-badge--thumbnail" aria-hidden="true">${DESIGN_BADGE_ICON}</span>`;

  /**
   * @param {string} url
   * @param {{ lazy?: boolean }} [options]
   */
  function buildThumbMfImg(url, options = {}) {
    const lazyAttr = options.lazy === false ? "" : " lazy";
    return `<mf-img class="product-media-thumb"${lazyAttr} alt="" style="--mf-img-object-fit: cover; --mf-img-object-position: center; --mf-img-height: 100%; --mf-img-bg: #fafafa;"><source src="${url}"></mf-img>`;
  }

  function findMediaGallery(root) {
    const section = root?.closest(".shopify-section") || document;
    return (
      section.querySelector("media-gallery") ||
      root?.closest("media-gallery") ||
      document.querySelector("media-gallery")
    );
  }

  function findCarousel(root) {
    const gallery = findMediaGallery(root);
    const section =
      gallery?.closest(".shopify-section") ||
      root?.closest(".shopify-section") ||
      document;

    return (
      gallery?.querySelector("product-media-swiper[ref='slideshow']") ||
      gallery?.querySelector("product-media-swiper") ||
      gallery?.querySelector('slideshow-component[ref="slideshow"]') ||
      gallery?.querySelector("slideshow-component") ||
      section.querySelector("product-media-swiper[ref='slideshow']") ||
      section.querySelector("product-media-swiper") ||
      section.querySelector('slideshow-component[ref="slideshow"]') ||
      section.querySelector("slideshow-component")
    );
  }

  function isSwiperCarousel(carousel) {
    return carousel?.tagName === "PRODUCT-MEDIA-SWIPER";
  }

  function getSlidesContainer(carousel) {
    if (!carousel) return null;
    if (isSwiperCarousel(carousel)) {
      return carousel.querySelector('[ref="slidesContainer"], .swiper-wrapper');
    }
    return carousel.querySelector("slideshow-slides");
  }

  function getVisibleSlides(slidesContainer) {
    if (!slidesContainer) return [];

    if (slidesContainer.classList.contains("swiper-wrapper")) {
      return Array.from(
        slidesContainer.querySelectorAll(".swiper-slide:not([hidden])")
      );
    }

    return Array.from(
      slidesContainer.querySelectorAll("slideshow-slide:not([hidden])")
    );
  }

  function findAiSlideByUrl(slidesContainer, url) {
    for (const slide of slidesContainer.querySelectorAll(
      '[data-ai-generated="true"], [data-slide-source="ai"], [data-slide-source="history"]'
    )) {
      const slideUrl =
        slide.getAttribute("data-ai-image-url") ||
        slide.querySelector("img.product-media__image, img")?.currentSrc ||
        slide.querySelector("img.product-media__image, img")?.src ||
        "";
      if (slideUrl === url) return slide;
    }
    return null;
  }

  function removeAiSlides(slidesContainer, exceptSlide) {
    const carousel = slidesContainer?.closest("product-media-swiper");

    slidesContainer
      .querySelectorAll(
        '[data-ai-generated="true"], [data-slide-source="ai"], [data-slide-source="history"]'
      )
      .forEach((slide) => {
        if (exceptSlide && slide === exceptSlide) return;

        const slideId = slide.dataset.slideId || slide.getAttribute("slide-id");
        if (
          carousel &&
          slideId &&
          typeof carousel.removeDynamicSlide === "function"
        ) {
          carousel.removeDynamicSlide(slideId);
          return;
        }

        slide.remove();
      });

    carousel?.removeThumbSlides?.('[data-ai-generated-control="true"]');
  }

  function removeAiControls(root) {
    root
      .querySelectorAll('[data-ai-generated-control="true"]')
      .forEach((node) => node.remove());
  }

  function getZoomDialog(gallery) {
    return gallery?.querySelector(
      'zoom-dialog[ref="zoomDialogComponent"], zoom-dialog'
    );
  }

  function isZoomEnabled(gallery) {
    return !!getZoomDialog(gallery);
  }

  function refreshZoomDialogRefs(zoomDialog) {
    if (zoomDialog && typeof zoomDialog.updatedCallback === "function") {
      zoomDialog.updatedCallback();
    }
  }

  function removeAiZoomEntries(gallery) {
    gallery
      ?.querySelectorAll('[data-ai-generated-zoom="true"]')
      .forEach((node) => node.remove());
    refreshZoomDialogRefs(getZoomDialog(gallery));
  }

  function getZoomMediaItemClass(gallery) {
    const existing = gallery?.querySelector(
      ".zoom-dialog-swiper__main .swiper-slide[ref], .zoom-dialog-swiper__main [ref='media[]']"
    );
    return (
      existing?.className ||
      "swiper-slide product-media-container constrain-height media-fit-contain product-media-container--image product-media-container--zoomable"
    );
  }

  function getSlideImageUrl(slide) {
    if (slide.hasAttribute("data-ai-image-url")) {
      return slide.getAttribute("data-ai-image-url") || "";
    }

    const img = slide.querySelector("img.product-media__image, img");
    return img?.currentSrc || img?.src || "";
  }

  function createZoomThumbSlide({ imageUrl, aspectRatio, aiGenerated = false }) {
    const slide = document.createElement("div");
    slide.className = "swiper-slide zoom-dialog-swiper__thumb";
    slide.style.setProperty("--aspect-ratio", String(aspectRatio));
    if (aiGenerated) {
      slide.setAttribute("data-ai-generated-zoom", "true");
      slide.setAttribute("aria-label", "Your Design preview");
    }
    slide.innerHTML = `${buildThumbMfImg(imageUrl)}${
      aiGenerated ? DESIGN_BADGE_THUMBNAIL_HTML : ""
    }`;
    return slide;
  }

  function createZoomThumbnailButton({ index, imageUrl, aspectRatio }) {
    return createZoomThumbSlide({
      imageUrl,
      aspectRatio,
      aiGenerated: true,
    });
  }

  function createNativeZoomThumbnailFromSlide(slide, index) {
    const imageUrl = getSlideImageUrl(slide);
    if (!imageUrl) return null;

    return createZoomThumbSlide({
      imageUrl,
      aspectRatio: getSlideAspectRatio(slide),
    });
  }

  function ensureZoomThumbnails(zoomDialog, slides) {
    const thumbnails = zoomDialog.querySelector('[ref="thumbsSlidesContainer"]');
    if (!thumbnails || slides.length <= 1) return;

    const aiSlide = slides.find(
      (slide) =>
        slide.hasAttribute("data-ai-generated") ||
        slide.dataset.slideSource === "ai" ||
        slide.dataset.slideSource === "history"
    );
    if (!aiSlide) return;

    const aiIndex = slides.indexOf(aiSlide);
    const aiUrl = getSlideImageUrl(aiSlide);
    if (!aiUrl || aiIndex < 0) return;

    thumbnails
      .querySelectorAll('[data-ai-generated-zoom="true"]')
      .forEach((node) => node.remove());

    if (thumbnails.children.length === 0) {
      slides.forEach((slide, index) => {
        if (slide.hasAttribute("data-ai-generated")) return;
        const thumb = createNativeZoomThumbnailFromSlide(slide, index);
        if (thumb) thumbnails.appendChild(thumb);
      });
    }

    const aiThumb = createZoomThumbSlide({
      imageUrl: aiUrl,
      aspectRatio: getSlideAspectRatio(aiSlide),
      aiGenerated: true,
    });
    const thumbSlides = Array.from(thumbnails.children);
    const thumbReference = thumbSlides[aiIndex] || null;

    if (thumbReference) {
      thumbnails.insertBefore(aiThumb, thumbReference);
    } else {
      thumbnails.appendChild(aiThumb);
    }
  }

  async function refreshZoomDialogSwipers(zoomDialog) {
    if (zoomDialog && typeof zoomDialog.refreshSwipers === "function") {
      await zoomDialog.refreshSwipers();
    }
  }

  async function syncAiZoomMedia(gallery, slides, aiSlide, aiIndex, url, aspectRatio, width, height) {
    const zoomDialog = getZoomDialog(gallery);
    const mediaList = zoomDialog?.querySelector('[ref="slidesContainer"]');
    if (!zoomDialog || !mediaList || !url) return;

    mediaList
      .querySelectorAll('[data-ai-generated-zoom="true"]')
      .forEach((node) => node.remove());

    const slide = document.createElement("div");
    slide.id = `product-ai-${aiSlide.getAttribute("slide-id")}`;
    slide.className = getZoomMediaItemClass(gallery);
    slide.setAttribute("ref", "media[]");
    slide.setAttribute("data-ai-generated-zoom", "true");
    slide.style.setProperty("--media-preview-ratio", String(aspectRatio));
    slide.innerHTML = `
      <div class="product-media-constraint-wrapper">
        <div class="product-media" style="--ratio: ${aspectRatio}">
          <img
            src="${url}"
            alt="Your Design preview"
            class="product-media__image"
            ref="image"
            loading="lazy"
            width="${width}"
            height="${height}"
          />
        </div>
      </div>
      ${DESIGN_BADGE_MAIN_HTML}
    `;

    const mediaSlides = Array.from(mediaList.querySelectorAll(".swiper-slide"));
    const referenceNode = mediaSlides[aiIndex] || null;
    if (referenceNode) {
      mediaList.insertBefore(slide, referenceNode);
    } else {
      mediaList.appendChild(slide);
    }

    ensureZoomThumbnails(zoomDialog, slides);
    refreshZoomDialogRefs(zoomDialog);
    await refreshZoomDialogSwipers(zoomDialog);
  }

  function syncAiZoom(gallery, slides) {
    if (!isZoomEnabled(gallery)) return;

    const aiSlide = slides.find(
      (slide) =>
        slide.hasAttribute("data-ai-generated") ||
        slide.dataset.slideSource === "ai" ||
        slide.dataset.slideSource === "history"
    );
    if (!aiSlide) return;

    const aiIndex = slides.indexOf(aiSlide);
    const aiUrl = getSlideImageUrl(aiSlide);
    if (aiIndex < 0 || !aiUrl) return;

    const img = aiSlide.querySelector("img");
    syncAiZoomMedia(
      gallery,
      slides,
      aiSlide,
      aiIndex,
      aiUrl,
      getSlideAspectRatio(aiSlide),
      img?.getAttribute("width") || 1200,
      img?.getAttribute("height") || 1200
    );
  }

  function reindexSlides(carousel, slidesContainer) {
    const slides = getVisibleSlides(slidesContainer);

    if (!isSwiperCarousel(carousel)) {
      slides.forEach((slide, index) => {
        slide.style.setProperty("--slideshow-timeline", `--slide-${index}`);
      });
      updateSlideshowTimeline(carousel, slides.length);
    }

    return slides;
  }

  function setCarouselMultiMode(carousel, slides) {
    const isMulti = slides.length > 1;

    if (isMulti) {
      carousel.classList.add("slideshow--has-multiple-slides");
      carousel.classList.remove(
        "slideshow--single-media",
        "product-media-gallery__slideshow--single-media"
      );
      carousel.removeAttribute("disabled");
      return;
    }

    carousel.classList.remove("slideshow--has-multiple-slides");
    carousel.classList.add(
      "slideshow--single-media",
      "product-media-gallery__slideshow--single-media"
    );
  }

  function loadImageDimensions(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth || 1200;
        const height = img.naturalHeight || 1200;
        resolve({
          width,
          height,
          aspectRatio: width && height ? width / height : 1,
        });
      };
      img.onerror = () =>
        resolve({ width: 1200, height: 1200, aspectRatio: 1 });
      img.src = url;
    });
  }

  function getTemplateSlide(slidesContainer) {
    const slideSelector = slidesContainer.classList.contains("swiper-wrapper")
      ? '.swiper-slide:not([data-ai-generated="true"]):not([hidden])'
      : 'slideshow-slide:not([data-ai-generated="true"]):not([hidden])';

    return slidesContainer.querySelector(slideSelector);
  }

  function getSlideAspectRatio(slide) {
    const inline = slide.getAttribute("style") || "";
    const match = inline.match(/--media-preview-ratio:\s*([^;]+)/);
    if (match) {
      const ratio = parseFloat(match[1]);
      if (Number.isFinite(ratio) && ratio > 0) return ratio;
    }

    const img = slide.querySelector("img");
    if (img?.naturalWidth && img?.naturalHeight) {
      return img.naturalWidth / img.naturalHeight;
    }

    return 1;
  }

  function updateSlideshowTimeline(carousel, slideCount) {
    if (isSwiperCarousel(carousel)) return;

    const scopes = [];
    for (let index = 0; index < slideCount; index++) {
      scopes.push(`--slide-${index}`);
    }

    const style = carousel.getAttribute("style") || "";
    const cleaned = style
      .replace(/--slideshow-timeline:\s*[^;]+;?/g, "")
      .trim();
    const timeline = `--slideshow-timeline: ${scopes.join(", ")};`;
    carousel.setAttribute(
      "style",
      cleaned ? `${timeline} ${cleaned}` : timeline
    );
  }

  function createSlideElement({
    url,
    slideId,
    aspectRatio,
    width,
    height,
    template,
    isSwiper,
  }) {
    const slide = document.createElement(isSwiper ? "div" : "slideshow-slide");
    slide.setAttribute("ref", "slides[]");
    slide.setAttribute("aria-hidden", "true");
    slide.setAttribute("slide-id", slideId);
    slide.setAttribute("data-slide-id", slideId);
    slide.setAttribute("data-slide-source", "ai");
    slide.className = isSwiper
      ? `swiper-slide ${template.className || "product-media-container product-media-container--image media-fit-contain"}`
      : template.className ||
        "product-media-container product-media-container--image media-fit-contain";
    slide.setAttribute("data-ai-generated", "true");
    slide.setAttribute("data-ai-image-url", url);

    const templateStyle =
      template.style || `--media-preview-ratio: ${aspectRatio};`;
    const extraStyle = isSwiper
      ? `--product-media-fit: contain; ${templateStyle}`
      : `--slideshow-timeline: --slide-placeholder; --product-media-fit: contain; ${templateStyle}`;

    slide.setAttribute("style", extraStyle);

    slide.innerHTML = `
      <div class="product-media-constraint-wrapper">
        <div class="product-media" style="--ratio: ${aspectRatio}">
          <img
            src="${url}"
            alt="Your Design preview"
            class="product-media__image"
            loading="lazy"
            width="${width}"
            height="${height}"
          />
        </div>
      </div>
      ${DESIGN_BADGE_MAIN_HTML}
    `;
    return slide;
  }

  function createThumbSlideElement({ imageUrl, aspectRatio }) {
    const slide = document.createElement("div");
    slide.className = "swiper-slide slideshow-controls__thumbnail";
    slide.setAttribute("data-ai-generated-control", "true");
    slide.setAttribute("aria-label", "Your Design preview");
    slide.style.setProperty("--aspect-ratio", String(aspectRatio));
    slide.innerHTML = `${buildThumbMfImg(imageUrl)}${DESIGN_BADGE_THUMBNAIL_HTML}`;
    return slide;
  }

  function createThumbnailButton({ index, imageUrl, aspectRatio }) {
    const button = document.createElement("button");
    button.type = "button";
    button.className =
      "slideshow-control button button-unstyled slideshow-controls__thumbnail";
    button.setAttribute("on:click", `/select/${index}`);
    button.setAttribute("ref", "thumbnails[]");
    button.setAttribute("aria-label", "Your Design preview");
    button.setAttribute("data-ai-generated-control", "true");
    button.style.setProperty("--aspect-ratio", String(aspectRatio));
    button.innerHTML = `${buildThumbMfImg(imageUrl)}${DESIGN_BADGE_THUMBNAIL_HTML}`;
    return button;
  }

  function createDotItem({ index, totalSlides }) {
    const li = document.createElement("li");
    li.setAttribute("data-ai-generated-control", "true");

    const button = document.createElement("button");
    button.type = "button";
    button.className = "slideshow-control button button-unstyled";
    button.style.setProperty("animation-timeline", `--slide-${index}`);
    button.setAttribute(
      "aria-label",
      `Slide ${index + 1} of ${totalSlides}`
    );
    button.setAttribute("on:click", `/select/${index}`);
    button.setAttribute("ref", "dots[]");
    button.setAttribute("data-ai-generated-control", "true");
    button.textContent = String(index + 1);
    li.appendChild(button);
    return li;
  }

  function syncAiThumbnail(carousel, gallery, slides) {
    const aiSlide = slides.find(
      (slide) =>
        slide.hasAttribute("data-ai-generated") ||
        slide.dataset.slideSource === "ai" ||
        slide.dataset.slideSource === "history"
    );
    if (!aiSlide) return;

    const aiIndex = slides.indexOf(aiSlide);
    const aiUrl = getSlideImageUrl(aiSlide);
    if (!aiUrl) return;

    if (isSwiperCarousel(carousel)) {
      carousel.removeThumbSlides?.('[data-ai-generated-control="true"]');

      const thumbSlide = createThumbSlideElement({
        imageUrl: aiUrl,
        aspectRatio: getSlideAspectRatio(aiSlide),
      });

      if (typeof carousel.appendThumbSlide === "function") {
        carousel.appendThumbSlide(thumbSlide);
      }
      return;
    }

    gallery.querySelectorAll(".slideshow-controls__thumbnails").forEach((thumbnails) => {
      thumbnails.appendChild(
        createThumbnailButton({
          index: aiIndex,
          imageUrl: aiUrl,
          aspectRatio: getSlideAspectRatio(aiSlide),
        })
      );
    });
  }

  function syncCounterAndDots(gallery, slides) {
    if (gallery.querySelector("product-media-swiper")) return;

    const totalSlides = slides.length;
    const aiSlide = slides.find((slide) =>
      slide.hasAttribute("data-ai-generated")
    );
    const aiIndex = aiSlide ? slides.indexOf(aiSlide) : -1;

    gallery.querySelectorAll("slideshow-controls").forEach((control) => {
      const counter = control.querySelector(".slideshow-controls__counter");
      if (counter) {
        const slash = counter.querySelector(".slash");
        if (slash) {
          let node = slash.nextSibling;
          while (node && node.nodeType !== Node.TEXT_NODE) {
            node = node.nextSibling;
          }
          if (node) {
            node.textContent = String(totalSlides);
          } else {
            counter.appendChild(document.createTextNode(String(totalSlides)));
          }
        }
      }

      const dotsList = control.querySelector(".slideshow-controls__dots");
      if (dotsList && aiIndex >= 0 && dotsList.children.length <= aiIndex) {
        dotsList.appendChild(createDotItem({ index: aiIndex, totalSlides }));
      }
    });
  }

  function rebuildCarouselControls(carousel, gallery, slides) {
    setCarouselMultiMode(carousel, slides);
    removeAiControls(gallery);
    removeAiZoomEntries(gallery);
    syncAiThumbnail(carousel, gallery, slides);
    syncCounterAndDots(gallery, slides);
    syncAiZoom(gallery, slides);
  }

  async function ensureCarouselInitialized(carousel, slide, { select = true } = {}) {
    if (typeof carousel.registerDynamicSlide !== "function") return false;

    if (typeof carousel.updatedCallback === "function") {
      carousel.updatedCallback();
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    await carousel.registerDynamicSlide(slide, {
      select,
      animate: true,
    });
    return true;
  }

  async function selectSlide(carousel, slide) {
    if (await ensureCarouselInitialized(carousel, slide, { select: true })) {
      return;
    }

    const slideId = slide.getAttribute("slide-id");
    if (slideId && typeof carousel.select === "function") {
      try {
        await carousel.select({ id: slideId }, undefined, { animate: true });
        return;
      } catch (error) {
        console.warn("[AiProductMedia] select by id failed", error);
      }
    }

    const slides =
      carousel.slides || getVisibleSlides(getSlidesContainer(carousel));
    const index = slides.indexOf(slide);
    if (index >= 0 && typeof carousel.select === "function") {
      await carousel.select(index, undefined, { animate: true });
    }
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function waitForCarouselReady(root, maxAttempts = 30) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const carousel = findCarousel(root);
      const slidesContainer = getSlidesContainer(carousel);

      if (carousel?.isConnected && slidesContainer) {
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        return { carousel, slidesContainer };
      }

      await sleep(50);
    }

    return null;
  }

  async function addImageToProductMedia(imageUrl, options = {}) {
    const { select = true, root = document } = options;
    if (!imageUrl) return null;

    const gallery = findMediaGallery(root);
    const ready = await waitForCarouselReady(root, 1);
    const carousel = ready?.carousel || findCarousel(root);
    const slidesContainer =
      ready?.slidesContainer || getSlidesContainer(carousel);
    const useSwiper = isSwiperCarousel(carousel);

    if (!carousel || !slidesContainer || !gallery) {
      console.warn("[AiProductMedia] carousel not found");
      return null;
    }

    const existingSameUrl = findAiSlideByUrl(slidesContainer, imageUrl);
    removeAiSlides(slidesContainer, existingSameUrl);
    removeAiControls(gallery);
    removeAiZoomEntries(gallery);

    let slides = reindexSlides(carousel, slidesContainer);

    if (existingSameUrl && existingSameUrl.isConnected) {
      rebuildCarouselControls(carousel, gallery, slides);
      if (slides.length > 1) {
        await ensureCarouselInitialized(carousel, existingSameUrl, { select });
      }
      window.dispatchEvent(new Event("resize"));
      return existingSameUrl;
    }

    const templateSlide = getTemplateSlide(slidesContainer);
    const dimensions = await loadImageDimensions(imageUrl);
    const slideId = `ai-${Date.now()}-${++aiSlideCounter}`;

    const slide = createSlideElement({
      url: imageUrl,
      slideId,
      aspectRatio: dimensions.aspectRatio,
      width: dimensions.width,
      height: dimensions.height,
      template: {
        className: templateSlide?.className?.replace(/\bswiper-slide\b/g, "").trim(),
        style: templateSlide?.getAttribute("style") || null,
      },
      isSwiper: useSwiper,
    });

    slidesContainer.appendChild(slide);

    if (typeof carousel.updatedCallback === "function") {
      carousel.updatedCallback();
    }

    slides = reindexSlides(carousel, slidesContainer);
    rebuildCarouselControls(carousel, gallery, slides);

    if (slides.length > 1) {
      await ensureCarouselInitialized(carousel, slide, { select });
    }

    window.dispatchEvent(new Event("resize"));
    return slide;
  }

  async function addImageToProductMediaWhenReady(imageUrl, options = {}) {
    const { maxAttempts = 6, root = document, ...rest } = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const ready = await waitForCarouselReady(root);
      if (!ready) {
        await sleep(80 * (attempt + 1));
        continue;
      }

      const result = await addImageToProductMedia(imageUrl, {
        ...rest,
        root,
        select: rest.select !== false,
      });

      if (result) {
        return result;
      }

      await sleep(80 * (attempt + 1));
    }

    return null;
  }

  function isMobileViewport() {
    return window.matchMedia("(max-width: 999px)").matches;
  }

  function getScrollTopOffset() {
    const styles = getComputedStyle(document.documentElement);
    const header = parseFloat(styles.getPropertyValue("--header-height"));
    const announcement = parseFloat(
      styles.getPropertyValue("--announcement-bar-height")
    );
    return (
      (Number.isFinite(header) ? header : 0) +
      (Number.isFinite(announcement) ? announcement : 0) +
      16
    );
  }

  function findProductMediaAnchor(root) {
    const section =
      root?.closest(".shopify-section") ||
      root?.closest(".product-information") ||
      document;

    return (
      section.querySelector(".product-information__media") ||
      section.querySelector("media-gallery") ||
      findCarousel(root) ||
      section.querySelector('[data-testid="product-information-media"]')
    );
  }

  function scrollToProductMedia(root) {
    if (!isMobileViewport()) return;

    const quickAddDialog = root.closest("quick-add-dialog");
    const dialogPanel = quickAddDialog?.querySelector("dialog[open]");

    const runScroll = () => {
      if (dialogPanel) {
        dialogPanel.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const anchor = findProductMediaAnchor(root);
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop || 0;

      if (anchor) {
        const targetY =
          anchor.getBoundingClientRect().top + scrollTop - getScrollTopOffset();
        window.scrollTo({
          top: Math.max(0, targetY),
          behavior: "smooth",
        });
        return;
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(runScroll);
    });
  }

  window.AiProductMedia = {
    addImageToProductMedia,
    addImageToProductMediaWhenReady,
    findSlideshow: findCarousel,
    findCarousel,
    scrollToProductMedia,
  };
})();
